import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  firebase,
  getToken,
  rtdb,
  sendPushNotification,
} from "../../../utils/firebase";
import { BASE_URL, environment } from "utils/constant";
import NoMessage from "assets/empty state/NoMessage.svg";
import SnagList from "./components/SnagList";
import ChatHeader from "./components/ChatHeader";
import MessageBubble from "./components/MessageBubble";
import MessageInput from "./components/MessageInput";
import ImageModal from "./components/ImageModal";
import { ChevronDown } from "lucide-react";

const SnagsChat = ({ id }) => {
  const { siteId: paramSiteId, snagRoomId } = useParams();
  const navigate = useNavigate();
  const siteId = paramSiteId || localStorage.getItem("site");
  const [snags, setSnags] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newSnagTitle, setNewSnagTitle] = useState("");
  const [showNewSnagForm, setShowNewSnagForm] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const autoScrollRef = useRef(true);
  const forceScrollToBottomRef = useRef(true);
  const scrollRafRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const lastScrollHeightRef = useRef(0);
  const [showChatrooms, setShowChatrooms] = useState(true);
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [activeSnag, setActiveSnag] = useState(null);
  const [activeGlobalChat, setActiveGlobalChat] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [lastMessages, setLastMessages] = useState({});
  const [generalMessages, setGeneralMessages] = useState([]);
  const [trigger, setTrigger] = useState(0);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [snagMembers, setSnagMembers] = useState({}); // { snagId: [userId,…] }
  const [showMemberModal, setShowMemberModal] = useState(null); // snagId or null
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [initialMemberIds, setInitialMemberIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // const siteId = localStorage.getItem("site");
  const siteData = JSON.parse(localStorage.getItem("siteData"));
  const currentUser = JSON.parse(localStorage.getItem("user"));

  // Push an in-app notification to RTDB so it shows in Notifications modal
  const pushInAppNotification = async ({ title, message, snagId, siteId, target_ids }) => {
    try {
      const organization = JSON.parse(
        localStorage.getItem("organization") || "{}"
      );
      const organizationId = organization?.id;
      if (!organizationId) return;

      await rtdb.ref(`notifications-${environment}/${organizationId}`).push({
        title,
        message,
        site_id: siteId,
        date: new Date().toISOString(),
        read: false,
        type: "snag_message",
        metadata: {
          snag_id: snagId,
          type: "new_message"
        },
        target_ids,
        actor_id: currentUser?.user,
        module: "Snags"
      });
    } catch (error) {
      console.error("Failed to push in-app notification:", error);
    }
  };

  // Function to mark messages as read
  const markMessagesAsRead = async (chatId, isGeneral = false) => {
    if (!currentUser?.user) return;

    const readPath = isGeneral
      ? `site_snags-${environment}/${siteId}/general_updates/read_by/${currentUser.user}`
      : `site_snags-${environment}/${siteId}/${chatId}/read_by/${currentUser.user}`;

    try {
      await rtdb.ref(readPath).set({
        lastReadAt: firebase.database.ServerValue.TIMESTAMP,
        userId: currentUser.user,
      });
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  // Function to calculate unread counts
  const calculateUnreadCount = (messages, readData) => {
    if (!readData || !currentUser?.user) return messages.length;

    const userReadData = readData[currentUser?.user];
    if (!userReadData || !userReadData.lastReadAt) return messages.length;

    return messages.filter(
      (msg) =>
        msg.createdAt > userReadData.lastReadAt &&
        msg.senderId !== currentUser.user
    ).length;
  };

  // Fetch team members
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(
          `${BASE_URL}/team/list-site-members/${siteId}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setTeamMembers(data.data || []);
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
      }
    };

    if (siteId) {
      fetchTeamMembers();
    }
  }, [siteId]);

  // Fetch snags list
  useEffect(() => {
    if (!siteId) return;

    const snagsRef = rtdb.ref(`site_snags-${environment}/${siteId}`);
    const handler = (snapshot) => {
      const snagsData = [];
      const membersMap = {};

      snapshot.forEach((child) => {
        const key = child.key;
        if (key !== "general_updates") {
          const val = child.val();
          snagsData.push({ id: key, ...val });

          // keep members map for quick lookup
          if (Array.isArray(val.members)) {
            membersMap[key] = val.members;
          } else {
            membersMap[key] = []; // treat as “open to all”
          }
        }
      });

      setSnags(snagsData.sort((a, b) => b.createdAt - a.createdAt));
      setSnagMembers(membersMap);

      // auto-select first snag if nothing selected
      if (!activeSnag && !activeGlobalChat && snagsData.length) {
        setActiveSnag(snagsData[0].id);
      }
    };

    snagsRef.on("value", handler);
    return () => snagsRef.off("value", handler);
  }, [siteId, activeSnag, activeGlobalChat]);

  // Merged useEffect for messages, unread counts, and last messages
  useEffect(() => {
    if (!siteId || !currentUser?.user) return;

    const unsubscribeFunctions = [];
    const unreadCountsObj = {};
    const lastMessagesObj = {};

    // Handle General Updates
    const generalMessagesRef = rtdb.ref(
      `site_snags-${environment}/${siteId}/general_updates/messages`
    );
    const generalReadRef = rtdb.ref(
      `site_snags-${environment}/${siteId}/general_updates/read_by`
    );

    const handleGeneralMessages = async (snapshot) => {
      const msgs = [];
      snapshot.forEach((childSnapshot) => {
        const msgData = childSnapshot.val();
        msgs.push({
          id: childSnapshot.key,
          ...msgData,
        });
      });

      setGeneralMessages(msgs);

      // Update last message for general updates
      if (msgs.length > 0) {
        const lastMsg = msgs[msgs.length - 1];
        lastMessagesObj["general-updates"] = {
          text: lastMsg.text || (lastMsg.has_media ? "Media" : ""),
          senderName: lastMsg.senderName,
          createdAt: lastMsg.createdAt,
        };
      }

      // Handle read status for general updates
      generalReadRef.on("value", (readSnapshot) => {
        const readData = readSnapshot.val() || {};
        unreadCountsObj["general-updates"] = calculateUnreadCount(
          msgs,
          readData
        );

        setUnreadCounts({ ...unreadCountsObj });
        setLastMessages({ ...lastMessagesObj });
      });
    };

    generalMessagesRef
      .orderByChild("createdAt")
      .on("value", handleGeneralMessages);
    unsubscribeFunctions.push(() => {
      generalMessagesRef.off();
      generalReadRef.off();
    });

    // Handle individual snags
    const handleSnagMessages = (snag) => {
      const snagMessagesRef = rtdb.ref(
        `site_snags-${environment}/${siteId}/${snag.id}/messages`
      );
      const snagReadRef = rtdb.ref(
        `site_snags-${environment}/${siteId}/${snag.id}/read_by`
      );

      const handleMessages = async (snapshot) => {
        const msgs = [];

        snapshot.forEach((childSnapshot) => {
          const msgData = childSnapshot.val();
          msgs.push({
            id: childSnapshot.key,
            ...msgData,
          });
        });

        // Sort messages by createdAt
        msgs.sort((a, b) => a.createdAt - b.createdAt);

        // Update messages for active snag
        if (snag.id === activeSnag) {
          setMessages(msgs);
        }

        // Update last message for this snag
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          lastMessagesObj[snag.id] = {
            text: lastMsg.text || (lastMsg.has_media ? "Media" : ""),
            senderName: lastMsg.senderName,
            createdAt: lastMsg.createdAt,
          };
        }

        // Handle read status
        snagReadRef.on("value", (readSnapshot) => {
          const readData = readSnapshot.val() || {};
          unreadCountsObj[snag.id] = calculateUnreadCount(msgs, readData);

          setUnreadCounts({ ...unreadCountsObj });
          setLastMessages({ ...lastMessagesObj });
        });
      };

      snagMessagesRef.orderByChild("createdAt").on("value", handleMessages);

      unsubscribeFunctions.push(() => {
        snagMessagesRef.off();
        snagReadRef.off();
      });
    };

    // Subscribe to all existing snags
    snags.forEach(handleSnagMessages);

    // Cleanup function
    return () => {
      unsubscribeFunctions.forEach((cleanup) => cleanup());
    };
  }, [siteId, currentUser?.user, snags, activeSnag, trigger]);

  const activeMessages = useMemo(() => {
    return activeGlobalChat === "general-updates" ? generalMessages : messages;
  }, [activeGlobalChat, generalMessages, messages]);

  const clearScheduledScroll = () => {
    if (scrollRafRef.current != null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
    if (scrollTimeoutRef.current != null) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  };

  const doScrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Hard-pin to absolute bottom (no smooth scrolling -> avoids oscillation/jitter).
    container.scrollTop = container.scrollHeight;
    lastScrollHeightRef.current = container.scrollHeight;
  };

  const scheduleScrollToBottom = () => {
    clearScheduledScroll();

    // Deterministic scroll on next paint (after DOM updates, before user notices).
    scrollRafRef.current = requestAnimationFrame(() => {
      doScrollToBottom();
      scrollRafRef.current = null;
    });

    // One tiny follow-up for late media/font/layout settling.
    scrollTimeoutRef.current = setTimeout(() => {
      const container = messagesContainerRef.current;
      if (!container) return;
      const shouldPin = forceScrollToBottomRef.current || autoScrollRef.current;
      if (shouldPin && container.scrollHeight !== lastScrollHeightRef.current) {
        doScrollToBottom();
      }
      scrollTimeoutRef.current = null;
    }, 60);
  };

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    autoScrollRef.current = distanceFromBottom < 120; // px threshold
    setShowScrollToBottom(distanceFromBottom > 240);
  };

  // Force-scroll when switching rooms
  useEffect(() => {
    forceScrollToBottomRef.current = true;
    setShowScrollToBottom(false);
  }, [activeSnag, activeGlobalChat]);

  // Auto-scroll (force on switch, otherwise only when user is already near-bottom)
  useLayoutEffect(() => {
    const hasActiveChat = !!activeSnag || activeGlobalChat === "general-updates";
    if (!hasActiveChat) return;

    const shouldScroll =
      forceScrollToBottomRef.current || autoScrollRef.current;
    if (!shouldScroll) return;

    scheduleScrollToBottom();
    forceScrollToBottomRef.current = false;

    return () => clearScheduledScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSnag, activeGlobalChat, activeMessages.length]);

  // If the input area grows/shrinks (reply banner, media preview, upload state),
  // keep the last message visible when user is already at the bottom.
  useLayoutEffect(() => {
    if (!autoScrollRef.current) return;
    scheduleScrollToBottom();
    return () => clearScheduledScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFiles.length, !!replyToMessage, isUploading]);

  // Mark messages as read when switching chats
  useEffect(() => {
    const roomIdToUse = snagRoomId || id;
    if (roomIdToUse === "general-updates") {
      setActiveGlobalChat("general-updates");
      setActiveSnag(null);
      markMessagesAsRead("general-updates", true);
    } else if (roomIdToUse) {
      setActiveSnag(roomIdToUse);
      setActiveGlobalChat(null);
      markMessagesAsRead(roomIdToUse, false);
    }
  }, [id, snagRoomId, currentUser?.user, siteId]);

  const handleRoomSelect = (roomId, isGeneral = false) => {
    if (isGeneral) {
      navigate(`/site/${siteId}/snags/general-updates`);
    } else {
      navigate(`/site/${siteId}/snags/${roomId}`);
    }
  };

  const scrollToBottom = () => {
    scheduleScrollToBottom();
  };

  const handleScrollToBottomClick = () => {
    // Force-pin to bottom even if user previously scrolled up
    forceScrollToBottomRef.current = true;
    autoScrollRef.current = true;
    setShowScrollToBottom(false);
    scheduleScrollToBottom();
  };

  // Keep pinned to bottom as message content height changes (media loads, fonts, etc.)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    let raf = null;
    const observer = new ResizeObserver(() => {
      const shouldPin = forceScrollToBottomRef.current || autoScrollRef.current;
      if (!shouldPin) return;
      if (raf != null) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        doScrollToBottom();
        raf = null;
      });
    });

    observer.observe(container);
    return () => {
      if (raf != null) cancelAnimationFrame(raf);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSnag, activeGlobalChat]);

  const handleCreateSnag = async () => {
    if (!newSnagTitle.trim()) return;

    const newSnag = {
      title: newSnagTitle,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      createdBy: currentUser?.user || "unknown",
      creatorName:
        `${currentUser?.first_name} ${currentUser?.last_name}` || "Anonymous",
      messages: {},
    };

    try {
      const snagsRef = rtdb.ref(`site_snags-${environment}/${siteId}`);
      const newSnagRef = await snagsRef.push(newSnag);
      setActiveSnag(newSnagRef.key);
      setNewSnagTitle("");
      setShowNewSnagForm(false);
    } catch (error) {
      console.error("Error creating snag:", error);
    }
  };

  const isOwnerOrAdmin = teamMembers.some(
    (m) =>
      m.user_id === currentUser?.user && (m.joined_as === "owner" || m.is_admin)
  );

  const visibleSnags = snags.filter((snag) => {
    if (isOwnerOrAdmin) return true; // see everything
    const members = snagMembers[snag.id] ?? [];
    return members.length === 0 || members.includes(currentUser?.user);
  });

  const saveSnagMembers = async (snagId) => {
    await rtdb
      .ref(`site_snags-${environment}/${siteId}/${snagId}/members`)
      .set(selectedMemberIds.length ? selectedMemberIds : null); // null = open to all
    setShowMemberModal(null);
    setSelectedMemberIds([]);
  };

  const deleteSnag = async (snagId, title) => {
    if (!isOwnerOrAdmin) return;
    try {
      await rtdb.ref(`site_snags-${environment}/${siteId}/${snagId}`).remove();
      if (activeSnag === snagId) {
        setActiveSnag(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Error deleting snag:", error);
    }
  };

  const renameSnag = async (snagId, newTitle) => {
    if (!isOwnerOrAdmin) return;
    const title = (newTitle || "").trim();
    if (!title) return;
    try {
      await rtdb
        .ref(`site_snags-${environment}/${siteId}/${snagId}`)
        .update({ title });
      setTrigger((p) => p + 1);
    } catch (error) {
      console.error("Error renaming snag:", error);
    }
  };

  const sendSnagMessage = async (text) => {
    if (!text.trim() && mediaFiles.length === 0) return;

    /* ---------- upload media ---------- */
    let mediaArray = [];
    if (mediaFiles.length) {
      setIsUploading(true);
      const accessToken = localStorage.getItem("access");
      mediaArray = await Promise.all(
        mediaFiles.map(async (file) => {
          const fd = new FormData();
          fd.append("site_id", siteId);
          fd.append(
            "snag_id",
            activeGlobalChat === "general-updates"
              ? "general-updates"
              : activeSnag
          );

          let type = "document";
          if (file.type.startsWith("image/")) type = "image";
          else if (file.type.startsWith("video/")) type = "video";
          else if (file.type.startsWith("audio/")) type = "audio";

          fd.append("type", type);

          if (type === "image") {
            fd.append("image", file);
          } else {
            fd.append("file", file);
          }

          const r = await fetch(`${BASE_URL}/core/uploadsnagobjects/`, {
            method: "POST",
            headers: { Authorization: `Bearer ${accessToken}` },
            body: fd,
          });
          const j = await r.json();
          return {
            url: j.data.image || j.data.file,
            type: type,
            mediaId: j.data.id,
          };
        })
      );
      setIsUploading(false);
      setMediaFiles([]);
    }

    /* ---------- build message object ---------- */
    const msg = {
      text: text,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      senderId: currentUser?.user,
      senderName:
        `${currentUser?.first_name} ${currentUser?.last_name}` || "Anonymous",
      media: mediaArray.length ? mediaArray : null,
      has_media: mediaArray.length > 0,
      replyTo: replyToMessage
        ? replyToMessage
        : null,
    };

    /* ---------- push message ---------- */
    const isGeneral = activeGlobalChat === "general-updates";
    const msgRef = isGeneral
      ? rtdb.ref(`site_snags-${environment}/${siteId}/general_updates/messages`)
      : rtdb.ref(`site_snags-${environment}/${siteId}/${activeSnag}/messages`);

    await msgRef.push(msg);
    setReplyToMessage(null);
    setTrigger((p) => p + 1);
    setTimeout(scrollToBottom, 100);

    /* ---------- NOTIFICATIONS ---------- */
    const membersForNotif = isGeneral
      ? teamMembers.map((m) => m.user_id) // general → all
      : snagMembers[activeSnag] && snagMembers[activeSnag].length
        ? snagMembers[activeSnag] // private snag
        : teamMembers.map((m) => m.user_id); // open snag → all

    const notif = {
      title: `${activeGlobalChat === "general-updates" ? "General Updates" : currentSnag?.title} - ${siteData?.site_name}`,
      body: `${currentUser?.first_name} ${currentUser?.last_name}: ${text}${mediaArray.length ? " (media)" : ""
        }`,
      data: {
        site_id: siteData.id,
        snag_id: isGeneral ? "general_updates" : activeSnag
      },
    };
    // send push to each member
    for (const uid of membersForNotif) {
      const token = await getToken(uid);
      if (token?.token && uid !== currentUser?.user)
        sendPushNotification(token.token, notif.title, notif.body, notif.data);
    }

    // in-app notification
    await pushInAppNotification({
      title: notif.title,
      message: notif.body,
      snagId: isGeneral ? "general_updates" : activeSnag,
      siteId: siteData.id,
      target_ids: membersForNotif,
    });
  };

  const handleDeleteMessage = async (msgId) => {
    const chatPath =
      activeGlobalChat === "general-updates"
        ? `site_snags-${environment}/${siteId}/general_updates/messages/${msgId}`
        : `site_snags-${environment}/${siteId}/${activeSnag}/messages/${msgId}`;
    await rtdb.ref(chatPath).remove();
  };

  const handleUpdateMessage = async (msgId, newText, replyTo) => {
    const chatPath =
      activeGlobalChat === "general-updates"
        ? `site_snags-${environment}/${siteId}/general_updates/messages/${msgId}`
        : `site_snags-${environment}/${siteId}/${activeSnag}/messages/${msgId}`;
    await rtdb.ref(chatPath).update({
      text: newText,
      replyTo: replyTo || null,
    });
    setTrigger((prev) => prev + 1);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const newMediaFiles = Array.from(files).filter(
        (file) =>
          file.type.startsWith("image/") || file.type.startsWith("video/")
      );

      if (newMediaFiles.length > 0) {
        setMediaFiles((prev) => [...prev, ...newMediaFiles]);
      }
    }
  };

  const currentSnag = snags.find((snag) => snag.id === activeSnag);

  return (
    <div className="flex h-full bg-gray-50 rounded-lg shadow-lg overflow-hidden relative">
      <SnagList
        activeGlobalChat={activeGlobalChat}
        setActiveGlobalChat={(val) => handleRoomSelect(val, true)}
        setActiveSnag={(val) => handleRoomSelect(val, false)}
        setTrigger={setTrigger}
        markMessagesAsRead={markMessagesAsRead}
        lastMessages={lastMessages}
        unreadCounts={unreadCounts}
        showChatrooms={showChatrooms}
        setShowChatrooms={setShowChatrooms}
        showNewSnagForm={showNewSnagForm}
        setShowNewSnagForm={setShowNewSnagForm}
        isOwnerOrAdmin={isOwnerOrAdmin}
        newSnagTitle={newSnagTitle}
        setNewSnagTitle={setNewSnagTitle}
        handleCreateSnag={handleCreateSnag}
        visibleSnags={visibleSnags}
        activeSnag={activeSnag}
        deleteSnag={deleteSnag}
        renameSnag={renameSnag}
      />

      <div className="flex-1 flex flex-col">
        <ChatHeader
          activeGlobalChat={activeGlobalChat}
          currentSnag={currentSnag}
          isOwnerOrAdmin={isOwnerOrAdmin}
          setSelectedMemberIds={setSelectedMemberIds}
          setInitialMemberIds={setInitialMemberIds}
          snagMembers={snagMembers}
          setShowMemberModal={setShowMemberModal}
        />

        {/* Messages Area */}
        {(currentSnag || activeGlobalChat === "general-updates") ? (
          <div
            ref={messagesContainerRef}
            className={`flex-1 overflow-y-auto p-4 bg-white text-blue-900 relative ${isDragging ? "border-4 border-dashed border-purple-500" : ""
              }`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onScroll={handleMessagesScroll}
          >
            {isDragging && (
              <div className="absolute inset-0 bg-purple-100 bg-opacity-90 flex items-center justify-center z-10 pointer-events-none">
                <div className="text-center">
                  <div className="text-6xl mb-4">📁</div>
                  <div className="text-xl font-bold text-purple-900">
                    Drop media files here
                  </div>
                  <div className="text-sm text-purple-700 mt-2">
                    Images and videos accepted
                  </div>
                </div>
              </div>
            )}

            {activeMessages.length === 0 ? (
              <div className="flex flex-col items-center h-full justify-center text-gray-500">
                <img src={NoMessage} alt="No Messages" className="h-44 w-44" />
                <div>No messages yet. Start the conversation!</div>
              </div>
            ) : (
              activeMessages.map((msg, index) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  previousMessage={index > 0 ? activeMessages[index - 1] : null}
                  currentUser={currentUser}
                  menuOpenFor={menuOpenFor}
                  setMenuOpenFor={setMenuOpenFor}
                  onDelete={handleDeleteMessage}
                  onUpdate={handleUpdateMessage}
                  setReplyToMessage={setReplyToMessage}
                  setShowImagePreview={setShowImagePreview}
                  setSelectedMedia={setSelectedMedia}
                  teamMembers={teamMembers}
                />
              ))
            )}

            {/* Scroll-to-bottom (WhatsApp-like) button */}
            {showScrollToBottom && activeMessages.length > 0 && (
              <button
                type="button"
                onClick={handleScrollToBottomClick}
                className="absolute bottom-4 right-4 z-20 w-10 h-10 rounded-full bg-purple-700 text-white shadow-lg hover:bg-purple-800 active:scale-95 transition flex items-center justify-center"
                title="Jump to latest message"
                aria-label="Jump to latest message"
              >
                <ChevronDown size={20} />
              </button>
            )}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white">
            <div className="text-gray-500">Select a chatroom to start messaging</div>
          </div>
        )}

        {/* Message Input */}
        {(currentSnag || activeGlobalChat === "general-updates") && (
          <MessageInput
            onSend={sendSnagMessage}
            replyToMessage={replyToMessage}
            setReplyToMessage={setReplyToMessage}
            teamMembers={teamMembers}
            isUploading={isUploading}
            mediaFiles={mediaFiles}
            setMediaFiles={setMediaFiles}
          />
        )}
      </div>

      {/* Click-away overlay for message action menu */}
      {menuOpenFor && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setMenuOpenFor(null)}
        />
      )}

      {showImagePreview && (
        <ImageModal
          imageUrl={selectedMedia}
          onClose={() => setShowImagePreview(false)}
        />
      )}

      {showMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-5 w-96 max-h-[80vh] overflow-y-auto">
            <h3 className="font-bold mb-3">Select members for this snag</h3>
            {/* Preview and quick actions */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Selected: <span className="font-medium">{selectedMemberIds.length}</span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    className="underline hover:text-purple-700"
                    onClick={() => setSelectedMemberIds(teamMembers.map((m) => m.user_id))}
                  >
                    Select all
                  </button>
                  <button
                    className="underline hover:text-purple-700"
                    onClick={() => setSelectedMemberIds([])}
                  >
                    Clear
                  </button>
                </div>
              </div>
              {selectedMemberIds.filter((id) => !initialMemberIds.includes(id)).length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">Will add:</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedMemberIds
                      .filter((id) => !initialMemberIds.includes(id))
                      .map((id) => {
                        const m = teamMembers.find((tm) => tm.user_id === id);
                        if (!m) return null;
                        return (
                          <span
                            key={id}
                            className="px-2 py-0.5 text-xs bg-green-50 text-green-800 border border-green-200 rounded-full"
                          >
                            {m.first_name} {m.last_name}
                          </span>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-1">
              {teamMembers.map((m) => {
                const checked = selectedMemberIds.includes(m.user_id);
                return (
                  <label
                    key={m.user_id}
                    className="flex items-center gap-2 cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setSelectedMemberIds((prev) =>
                          checked
                            ? prev.filter((id) => id !== m.user_id)
                            : [...prev, m.user_id]
                        );
                      }}
                      className="rounded"
                    />
                    <span>
                      {m.first_name} {m.last_name}
                      {m.joined_as === "owner" && " (owner)"}
                      {m.is_admin &&
                        !m.joined_as?.includes("owner") &&
                        " (admin)"}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowMemberModal(null)}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() => saveSnagMembers(showMemberModal)}
                className="px-3 py-1 text-sm bg-purple-700 text-white rounded hover:bg-purple-800"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SnagsChat;